import React, { Fragment, ReactNode, useMemo } from 'react';
import FormInputStyleProperties from '@app/component/form/input/FormInputStyleProperties';
import Utilities from '@util/Utilities';
import _isEmpty from 'lodash/isEmpty';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useFieldContext, useFormContext } from '@app/site/_form';
import { FieldErrors } from '@app/site/_form/field/FieldErrors';

type MultiSelectFieldProps<TValue> = {
  placeholder?: string,
  label?: string,
  labelHint?: string,
  disabled?: boolean,
  className?: string,
  extraClassName?: string,
  hideErrorMessage?: boolean,
  options?: Option<TValue>[]
  portal?: boolean,
  groupOptions?: GroupOptions<TValue>[],
  onValueChange?: (val?: TValue[]) => any,
  optionsTextSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  size?: 'w-full' | 'small' | 'medium' | 'large' | 'xlarge'
  optionsSize?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge',
  maxSelections?: number,
  displayThreshold?: number,
} & FormInputStyleProperties;

type Option<TValue> = {
  id: string,
  label: string | ReactNode,
  value: TValue,
  extraInfo?: string,
  iconArea?: ReactNode
}

type GroupOptions<TValue> = {
  label: string,
  options: Option<TValue>[]
}

const sizeToWidthClass: Record<string, string> = {
  'w-full': 'w-full',
  small: 'w-20',
  medium: 'w-32',
  large: 'w-56',
  xlarge: 'w-64',
};

const optionsSizeToWidthClass: Record<string, string> = {
  ...sizeToWidthClass,
  xxlarge: 'w-86',
};

export const MultiSelectField = React.memo(function MultiSelectField<TValue extends any>(
  {
    placeholder,
    label,
    isLoading,
    disabled = false,
    className,
    extraClassName,
    labelHint,
    hideErrorMessage = false,
    options,
    portal,
    groupOptions,
    optionsTextSize = 'sm',
    size = 'large',
    optionsSize,
    maxSelections,
    displayThreshold = 3,
  }: MultiSelectFieldProps<TValue>) {

  const field = useFieldContext<TValue[]>();
  const form = useFormContext();

  const optionsMap = useMemo(() => {
    const map = new Map<TValue, Option<TValue>>();
    if (options) {
      options.forEach(option => map.set(option.value, option));
    }
    if (groupOptions) {
      groupOptions.forEach(group => {
        group.options.forEach(option => map.set(option.value, option));
      });
    }
    return map;
  }, [options, groupOptions]);

  const getSelectedOptionsText = (vals: TValue[]): string => {
    if (_isEmpty(vals)) {
      return placeholder || '';
    }
    if (vals.length > displayThreshold) {
      return `${vals.length} selected`;
    }
    return vals.map(val => optionsMap.get(val)?.label).filter(Boolean).join(', ');
  };

  const width = sizeToWidthClass[size] ?? 'w-56';
  const optionsWidth = optionsSize ? (optionsSizeToWidthClass[optionsSize] ?? width) : width;

  const hasError = (field.state.meta.isTouched || form.state.submissionAttempts > 0) && !field.state.meta.isValid;

  const renderField = () => {
    return <>
      <div>
        <Listbox value={field.state.value} onChange={(val) => field.handleChange(val)} disabled={disabled} multiple>
          {({ open }) => (
            <div>
              <div className="flex">
                {!!label && <div className="label">{label}</div>}
                {!!labelHint ? <span className="text-xs leading-6 ml-2 text-gray-500 italic">{labelHint}</span> : ''}
              </div>

              <div className="relative mt-0.5">
                <ListboxButton
                  className={Utilities.classNames(
                    !!className ? className : 'input relative cursor-default pl-4 bg-white',
                    Utilities.NVL(hasError, 'input-error'),
                    Utilities.NVL(isLoading, 'input-isLoading'),
                    Utilities.NVL(disabled, 'input-disabled'),
                    !hasError ? 'bg-white' : '',
                    'focus:outline-none',
                    'pr-4',
                    width,
                    extraClassName)
                }>
                  <span className="inline-flex w-full truncate">
                    <span
                      className={Utilities.classNames(hasError && !!placeholder ? 'text-slate-400' : '', 'truncate')}>{getSelectedOptionsText(field.state.value)}</span>
                  </span>
                  <span
                    className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <ListboxOptions style={{ zIndex: 1000 }}
                                anchor="bottom start"
                                portal={portal}
                                className={`z-100 mt-1 max-h-60 overflow-auto rounded-md bg-white py-2 text-${optionsTextSize} shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${optionsWidth}`}>
                  {renderOptions()}
                </ListboxOptions>
              </div>
            </div>
          )}
        </Listbox>
      </div>
    </>;
  };

  const renderOption = (o: Option<TValue>) => {
    const isSelected = field.state.value?.includes(o.value);
    const maxReached = !!maxSelections && (field.state.value?.length ?? 0) >= maxSelections;
    const isDisabled = maxReached && !isSelected;

    return <ListboxOption
      key={o.id}
      className={({ focus }) =>
        Utilities.classNames(
          focus && !isDisabled ? 'bg-gray-100' : '',
          'relative cursor-default select-none py-2 px-3',
          isDisabled ? 'opacity-50 cursor-not-allowed' : '',
        )
      }
      value={o.value}
      disabled={isDisabled}
    >
      {({ selected }) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={selected}
            readOnly
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span
            className={Utilities.classNames(selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate')}>
            {o.label}
          </span>
        </div>
      )}
    </ListboxOption>;
  };

  const renderOptions = () => {
    return <>
      {!_isEmpty(options) ? options!.map(o => renderOption(o)) : ''}
      {!_isEmpty(groupOptions) ? groupOptions!.map((go: GroupOptions<TValue>) => {
        return go.options!.map((o: Option<TValue>, oidx: number) => {
          return <React.Fragment key={o.id}>
            {oidx === 0 &&
              <p
                className="block bg-gray-50 border-b border-gray-100 py-1.5 px-4 text-xs font-bold text-blue-800">&bull; {go.label}</p>}
            {renderOption(o)}
          </React.Fragment>;
        });
      }) : ''}
    </>;
  };

  return <>
    {renderField()}
    {!hideErrorMessage && <FieldErrors meta={field.state.meta} />}
  </>;

});
